import { io } from "socket.io-client";
import CryptoJS from "crypto-js";
import html2canvas from "html2canvas";
// import UAParser from "ua-parser-js";
import * as UAParserPkg from "ua-parser-js";
const UAParser = UAParserPkg.UAParser;

function uuid() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return Math.random().toString(16).slice(2) + Date.now().toString(16);
}

class TwinalyzeAnalyticsImpl {
  constructor() {
    this.cfg = null;

    this.socket = null;
    this.socketReady = false;

    this.deviceId = null;
    this.sessionId = null; // uniqueSessionId (client generated)

    this.sessionReady = false;      // sessionCreate done
    this._booting = false;          // handshake in progress
    this._disabled = false;         // STOP state

    this.pendingEvents = [];        // queued until sessionReady
    this.scrollFired = false;

    this._initialized = false;

    this._sentSessionUpdate = false; // one-time
    this._sentUserUpdate = false;    // one-time

    this.indexId = 0;
    this._indexKey = null;

    this._lastShotAt = 0;
    this._shotInFlight = false;
  }

  // -------------------------
  // PUBLIC API
  // -------------------------
  init(cfg) {
    if (typeof window === "undefined") return;
    if (this._initialized) return; // strict-mode safe
    this._initialized = true;

    const organization = cfg.organization || cfg.orgId;

    const DEFAULT_SCREEN_ACTIVITY = {
      enabled: false,
      apiUrl: cfg.screenActivity?.apiUrl ?? `${cfg.socketUrl}/api/app/screenActivity/screenActivityDetails_post`,
      captureOn: ["page_view", "click", "form_start", "form_submit", "scroll", "view_search_results", "custom_event"],
      throttleMs: 2000,
      jpegQuality: 0.7,
    };

    // support: screenshots: true  OR  screenActivity: true  OR  screenActivity: { ... }
    const sa =
      typeof cfg.screenActivity === "object"
        ? { ...DEFAULT_SCREEN_ACTIVITY, ...cfg.screenActivity }
        : { ...DEFAULT_SCREEN_ACTIVITY };

    this.cfg = {
      apiKey: cfg.apiKey,
      organization,
      socketUrl: "https://api.twinalyze.com",
      // socketUrl: "http://192.168.1.26:3000",

      debug: true,

      // encryption like Android (AES key = socket.id)
      encrypt: true, // default true

      // session id from client side
      persistSession: cfg.persistSession !== false, // default true (sessionStorage)
      sessionKey: "twinalyze_session_id",

      // server response event naming
      // we listen to multiple possible response events to be safe:
      //   `${event}:res`, `${event}Res`, `${event}Response`, `${event}`
      responseSuffix: cfg.responseSuffix || ":res",
      responseTimeoutMs: cfg.responseTimeoutMs || 15000,

      // eventAdd fields
      source: cfg.source || "web",
      indexId: cfg.indexId ?? null,

      // auto tracking options
      enhancedMeasurement: {
        pageViews: true,
        scrolls: true,
        outboundClicks: true,
        siteSearch: { params: ["q", "s", "search", "query"] },
        formInteractions: true,
        fileDownloads: { extensions: ["pdf", "zip", "apk", "doc", "docx", "xls", "xlsx", "ppt", "pptx"] },
        ...(cfg.enhancedMeasurement || {}),
      },
      apiBaseUrl: cfg.apiBaseUrl || cfg.endpoint || cfg.socketUrl, // http base for REST APIs
      screenActivity: sa,
      // screenActivity: {
      //   enabled: cfg.screenActivity?.enabled ?? true,
      //   apiUrl: cfg.screenActivity?.apiUrl ?? `${cfg.socketUrl}/api/app/screenActivity/screenActivityDetails_post`,
      //   jpegQuality: cfg.screenActivity?.jpegQuality ?? 0.7,
      //   throttleMs: cfg.screenActivity?.throttleMs ?? 2000, // don't screenshot every click instantly
      //   capture: cfg.screenActivity?.capture ?? "viewport", // "viewport" | "fullpage"
      //   maxWidth: cfg.screenActivity?.maxWidth ?? 1280,     // reduce size
      // },
    };

    if (!this.cfg.apiKey || !this.cfg.organization) {
      console.log("[Twinalyze] ❌ Missing apiKey / organization / socketUrl");
      return;
    }

    this.deviceId = this._getOrCreateDeviceId();

    if (this.cfg.persistSession) {
      this.sessionId = sessionStorage.getItem(this.cfg.sessionKey) || null;
    }

    this._connectSocket();
    this._setupEnhancedMeasurement();
  }

  // custom event (web dev only uses this)
  track(eventName, properties = {}) {
    if (this._disabled) return;

    if (this.cfg?.debug) console.log("[Twinalyze][CUSTOM]", eventName, properties);

    if (!this.sessionReady) {
      this.pendingEvents.push({ eventName, properties: { ...this._pageContext(), ...properties }, ts: Date.now(), eventType: "custom" });
      return;
    }

    this._emitEventAdd(eventName, { ...this._pageContext(), ...properties }, "custom");
  }

  // -------------------------
  // SOCKET
  // -------------------------
  _connectSocket() {
    if (this.socket) return;

    const options = ({
      transports: ["websocket"], // Force WebSocket transport
      reconnection: true,
      reconnectionAttempts: Infinity, // Keep reconnecting indefinitely
      reconnectionDelay: 2000, // 2 seconds delay between reconnection attempts
      reconnectionDelayMax: 10000, // Max reconnection delay (10 seconds)
      timeout: 20000, // Connection timeout (20 seconds)
      path: "/socket.io/",
      auth: {
        "x-api-key": this.cfg.apiKey,
        "x-organization-id": this.cfg.organization,
        "x-app-version-name": this.cfg.appVersionName || "not_found",
        "x-analytics-sdk-version": this.cfg.analyticsSdkVersion || this.cfg.version || "1.0.0",
        "x-ad-analytics-sdk-version": this.cfg.adAnalyticsSdkVersion || "not_found",
        "x-app-package-name": this.cfg.appPackageName || location.hostname,
        "x-platform": "web",
        "x-sdk-version": this.cfg.version || "1.0.0",
      },
      extraHeaders: {
        "Origin": this.cfg.socketUrl,
        "x-api-key": this.cfg.apiKey,
        "x-organization-id": this.cfg.organization,
        "x-app-version-name": this.cfg.appVersionName || "not_found",
        "x-analytics-sdk-version": this.cfg.analyticsSdkVersion || this.cfg.version || "1.0.0",
        "x-ad-analytics-sdk-version": this.cfg.adAnalyticsSdkVersion || "not_found",
        "x-app-package-name": this.cfg.appPackageName || location.hostname,
        "x-platform": "web",
        "x-sdk-version": this.cfg.version || "1.0.0",
      },
      methods: ["GET", "POST"],
    })

    this.socket = io(this.cfg.socketUrl, options);

    this.socket.on("connect", async () => {
      this.socketReady = true;

      if (this.cfg.debug) {
        console.log("[Twinalyze][SOCKET] ✅ connected CHANGED NEW 1", {
          id: this.socket.id,
          transport: this.socket.io.engine.transport.name,
        });
      }

      await this._startFlow();
    });

    this.socket.on("disconnect", (reason) => {
      this.socketReady = false;

      // on reconnect, flow should run again (sessionCreate + one-time updates)
      this.sessionReady = false;
      this._booting = false;
      this._sentSessionUpdate = false;
      this._sentUserUpdate = false;
      this._resetIndexId();

      if (this.cfg.debug) console.log("[Twinalyze][SOCKET] ❌ disconnected:", reason);
    });

    this.socket.on("connect_error", (err) => {
      this.socketReady = false;
      if (this.cfg.debug) console.log("[Twinalyze][SOCKET] ❌ connect_error:", err?.message || err);
    });

    // Listening to sdk_upgrade_warning
    this.socket.on("sdk_upgrade_warning", (data) => {
      if (data && data.message) {
        console.log("SDK Upgrade Warning:", data.message);

        // Show the upgrade message
        alert(data.message);

        // Optionally, check for updateUrl and redirect
        if (data.updateUrl) {
          const proceedToUpdate = confirm("A new SDK version is available. Do you want to upgrade now?");

          if (proceedToUpdate) {
            window.location.href = data.updateUrl; // Redirect to the update page
          }
        }
      } else {
        console.error("Received invalid data in sdk_upgrade_warning event:", data);
      }
    });

    // optional incoming debug
    // this.socket.onAny((event, ...args) => {
    //   if (this.cfg.debug) console.log("[Twinalyze][IN]", event, args);
    // });
  }

  _initIndexId() {
    // call only when sessionId is available
    this._indexKey = `twinalyze_event_index_${this.sessionId}`;

    // always start from 0 for new session
    // if you want resume on refresh within same session, load from sessionStorage
    const stored = sessionStorage.getItem(this._indexKey);
    this.indexId = stored ? parseInt(stored, 10) : 0;
    if (Number.isNaN(this.indexId)) this.indexId = 0;
  }

  _nextIndexId() {
    const id = this.indexId;
    this.indexId += 1;

    if (this._indexKey) {
      sessionStorage.setItem(this._indexKey, String(this.indexId));
    }
    return id; // ✅ return current id then increment
  }

  _resetIndexId() {
    this.indexId = 0;
    if (this._indexKey) sessionStorage.removeItem(this._indexKey);
    this._indexKey = null;
  }

  _normalizeHttpBase(url) {
    return String(url || "")
      .replace(/^ws:\/\//, "http://")
      .replace(/^wss:\/\//, "https://")
      .replace(/\/$/, "");
  }

  // -------------------------
  // YOUR EXACT FLOW
  // -------------------------
  async _startFlow() {
    if (this._disabled) return;
    if (!this.socketReady) return;
    if (this._booting) return;

    this._booting = true;

    try {
      // 1) user|userCheckApp  { apiKey, organization }
      const checkRes = await this._request("user|userCheckApp", {
        apiKey: this.cfg.apiKey,
        organization: this.cfg.organization,
      });

      if (this.cfg.debug) console.log("[Twinalyze][FLOW] userCheckRes:", checkRes);

      // STOP condition:
      // "if success true and status 0 then next else STOP"
      const ok =
        checkRes &&
        checkRes.success === true &&
        (checkRes.status === 1 || checkRes.status === "1");

      const serverScreenshot = !!checkRes?.debugScreenshotCapture;
      this.cfg.screenActivity.enabled = serverScreenshot;

      if (!ok) {
        this._disabled = true;
        this.sessionReady = false;
        this.cfg.screenActivity.enabled = false;
        if (this.cfg.debug) console.log("[Twinalyze][FLOW] 🛑 STOP (userCheck failed)");
        return;
      }

      // 2) user|userExistsApp  { apiKey, deviceId, organization }
      const existsRes = await this._request("user|userExistsApp", {
        apiKey: this.cfg.apiKey,
        deviceId: this.deviceId,
        organization: this.cfg.organization,
      });

      if (this.cfg.debug) console.log("[Twinalyze][FLOW] userExistsRes:", existsRes);

      // your diagram says: if success===true => user exists
      const userExists = !!(existsRes && (existsRes.success === true) && (existsRes.userRegister === true));

      // 3) if user not exists => user|web|userCreateApp { apiKey, deviceId, properties, organization }
      if (!userExists) {
        const createRes = await this._request("user|web|userCreateApp", {
          apiKey: this.cfg.apiKey,
          deviceId: this.deviceId,
          properties: this._userProperties(),
          organization: this.cfg.organization,
        });

        if (this.cfg.debug) console.log("[Twinalyze][FLOW] userCreateRes:", createRes);

        // if backend returns explicit failure, stop
        // if (createRes && createRes.success === false) {
        //   this._disabled = true;
        //   this.sessionReady = false;
        //   if (this.cfg.debug) console.log("[Twinalyze][FLOW] 🛑 STOP (userCreate failed)");
        //   return;
        // }


        if (createRes && createRes.success === false) {
  console.log("[Twinalyze][FLOW] 🛑 userCreate failed, continuing...");
  // Allow the SDK to continue processing events even if user creation fails
  this.sessionReady = true; // Manually set session as ready
  // Do NOT disable SDK, let events continue flowing
}

      }

      // 4) create session id from client (your requirement)
      if (!this.sessionId) {
        this.sessionId = `sess_${uuid()}`;
        if (this.cfg.persistSession) {
          sessionStorage.setItem(this.cfg.sessionKey, this.sessionId);
        }
      }

      // 5) session|web|sessionCreateApp
      // { apiKey, deviceId, socketId, uniqueSessionId, deviceProperties, organization }
      const sessionRes = await this._request("session|web|sessionCreateApp", {
        apiKey: this.cfg.apiKey,
        deviceId: this.deviceId,
        socketId: this.socket.id,
        uniqueSessionId: this.sessionId,
        deviceProperties: this._deviceProperties(),
        organization: this.cfg.organization,
      });

      if (this.cfg.debug) console.log("[Twinalyze][FLOW] sessionCreateRes:", sessionRes);

      if (sessionRes && sessionRes.success === false) {
        this._disabled = true;
        this.sessionReady = false;
        return;
      }

      // Mark ready even if backend doesn't return anything useful
      this.sessionReady = true;
      this._initIndexId();

      // // 6) one-time updates (exact payloads)
      // this._emitSessionUpdateOnce();
      // this._emitUserUpdateOnce();

      // 7) flush queued auto/custom events
      this._flushPendingEvents();
    } finally {
      this._booting = false;
    }
  }

  // -------------------------
  // REQUEST/RESPONSE (NO ACK)
  // waits for server response event using "on/once"
  // -------------------------
  _request(eventName, payloadObj) {
    const timeoutMs = this.cfg.responseTimeoutMs;

    const candidates = [];
    const suffix = this.cfg.responseSuffix;

    // Try multiple common response event names (so you don’t have to guess)
    candidates.push(`${eventName}${suffix}`);      // user|userCheckApp:res
    candidates.push(`${eventName}Res`);            // user|userCheckAppRes
    candidates.push(`${eventName}Response`);       // user|userCheckAppResponse
    candidates.push(eventName);                    // same event name as response (some backends do this)

    // unique
    const resEvents = [...new Set(candidates)];

    return new Promise((resolve) => {
      if (!this.socketReady) return resolve(null);

      let done = false;

      const cleanup = () => {
        resEvents.forEach((ev) => this.socket.off(ev, onRes));
        clearTimeout(timer);
      };

      const onRes = (res) => {
        if (done) return;
        done = true;
        cleanup();
        resolve(res);
      };

      // IMPORTANT: register listeners before emit (so we don't miss fast responses)
      resEvents.forEach((ev) => this.socket.on(ev, onRes));

      const timer = setTimeout(() => {
        if (done) return;
        done = true;
        cleanup();
        resolve(null);
      }, timeoutMs);

      const sendData = this.cfg.encrypt ? this._encrypt(payloadObj) : payloadObj;
      this.socket.emit(eventName, sendData);
    });
  }

  _encrypt(obj) {
    const CRYPTOJS_SECRET_KEY = "3Xn8vQp2Lm9sAa7ZkT1yCw5eR0uH6dJ4"
    // AES key = socket.id (matches your Android/backend style)
    return CryptoJS.AES.encrypt(JSON.stringify(obj), CRYPTOJS_SECRET_KEY).toString();
    // return CryptoJS.AES.encrypt(JSON.stringify(obj), this.socket.id).toString();
  }

  async _waitForRenderStable() {
    // 2 frames
    await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));

    // fonts
    try { if (document.fonts?.ready) await document.fonts.ready; } catch { }

    // images
    const imgs = Array.from(document.images || []).filter(img => !img.complete);
    await Promise.allSettled(
      imgs.map(img => new Promise(res => {
        img.addEventListener("load", res, { once: true });
        img.addEventListener("error", res, { once: true });
      }))
    );
  }

  async _captureViewportJpegBlob() {
    await this._waitForRenderStable();

    const isViewport = (this.cfg.screenActivity?.capture || "viewport") === "viewport";
    const scale = Math.min(2, window.devicePixelRatio || 1);

    const canvas = await html2canvas(document.documentElement, {
      useCORS: true,
      allowTaint: false,
      backgroundColor: "#ffffff",
      logging: false,

      ...(isViewport
        ? { x: window.scrollX, y: window.scrollY, width: window.innerWidth, height: window.innerHeight }
        : { x: 0, y: 0, width: document.documentElement.scrollWidth, height: document.documentElement.scrollHeight }
      ),

      scale,
      onclone: (doc) => {
        const style = doc.createElement("style");
        style.textContent = `
          * { animation: none !important; transition: none !important; caret-color: transparent !important; }
        `;
        doc.head.appendChild(style);
      },
    });

    // downscale big screenshots (optional but helpful)
    const maxW = this.cfg.screenActivity?.maxWidth ?? 1280;
    if (canvas.width > maxW) {
      const ratio = maxW / canvas.width;
      const c2 = document.createElement("canvas");
      c2.width = Math.round(canvas.width * ratio);
      c2.height = Math.round(canvas.height * ratio);
      c2.getContext("2d").drawImage(canvas, 0, 0, c2.width, c2.height);
      return await new Promise((resolve) =>
        c2.toBlob(resolve, "image/jpeg", this.cfg.screenActivity?.jpegQuality ?? 0.7)
      );
    }

    return await new Promise((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", this.cfg.screenActivity?.jpegQuality ?? 0.7)
    );
  }

  async _uploadScreenActivity({ eventName, properties }) {
    if (!this.cfg.screenActivity?.enabled) return;
    if (!this.sessionReady) return;

    const now = Date.now();
    const throttleMs = this.cfg.screenActivity?.throttleMs ?? 2000;
    if (now - this._lastShotAt < throttleMs) return;
    if (this._shotInFlight) return;

    this._shotInFlight = true;
    this._lastShotAt = now;

    try {
      const blob = await this._captureViewportJpegBlob();
      if (!blob) return;

      const fd = new FormData();
      fd.append("apiKey", this.cfg.apiKey);
      fd.append("organization", this.cfg.organization);
      fd.append("screenName", document.title || "unknown");
      fd.append("identifier", JSON.stringify(properties || {})); // ✅ you asked: identifier = properties object
      fd.append("description", eventName || "event");
      fd.append("image", blob, `shot_${Date.now()}.jpg`);

      await fetch(this.cfg.screenActivity.apiUrl, {
        method: "POST",
        body: fd,
        keepalive: true,
      });
    } catch (e) {
      if (this.cfg.debug) console.log("[Twinalyze][SCREENSHOT] error:", e?.message || e);
    } finally {
      this._shotInFlight = false;
    }
  }

  // -------------------------
  // EMITS (exact payloads you gave)
  // -------------------------
  _emitSessionUpdateOnce() {
    if (!this.sessionReady) return;
    if (this._sentSessionUpdate) return;
    this._sentSessionUpdate = true;

    const payload = {
      apiKey: this.cfg.apiKey,
      deviceId: this.deviceId,
      deviceProperties: this._deviceProperties(),
      organization: this.cfg.organization,
    };

    const sendData = this.cfg.encrypt ? this._encrypt(payload) : payload;
    this.socket.emit("session|sessionUpdateApp", sendData);

    if (this.cfg.debug) console.log("[Twinalyze] ✅ sessionUpdate sent");
  }

  _emitUserUpdateOnce() {
    if (!this.sessionReady) return;
    if (this._sentUserUpdate) return;
    this._sentUserUpdate = true;

    const payload = {
      apiKey: this.cfg.apiKey,
      deviceId: this.deviceId,
      properties: this._userProperties(),
      organization: this.cfg.organization,
    };

    const sendData = this.cfg.encrypt ? this._encrypt(payload) : payload;
    this.socket.emit("user|userUpdateApp", sendData);

    if (this.cfg.debug) console.log("[Twinalyze] ✅ userUpdate sent");
  }

  _emitEventAdd(eventName, properties = {}, eventType = "auto") {
    if (!this.sessionReady) return;

    // if session just got ready and index not set
    if (this._indexKey == null) this._initIndexId();
    const indexId = this._nextIndexId();

    const payload = {
      socketId: this.socket.id,
      date: new Date().toISOString(),
      eventType: eventType,
      uniqueSessionId: this.sessionId,
      deviceId: this.deviceId,
      eventName,
      properties: properties,
      apiKey: this.cfg.apiKey,
      source: this.cfg.source,
      // ✅ auto index
      indexId,
      organization: this.cfg.organization,
    };

    console.log("payload session|web|sessionEventAddApp", payload);

    const sendData = this.cfg.encrypt ? this._encrypt(payload) : payload;
    this.socket.emit("session|web|sessionEventAddApp", sendData);

    // this._maybeUploadScreenActivity(eventName, properties, indexId).catch(() => { });
    this._uploadScreenActivity({ eventName, properties });

    if (this.cfg.debug) console.log("[Twinalyze][EVENT_ADD]", eventName, properties, indexId);
  }

  _flushPendingEvents() {
    if (!this.sessionReady) return;
    if (!this.pendingEvents.length) return;

    const items = this.pendingEvents.splice(0, this.pendingEvents.length);
    items.forEach((e) => this._emitEventAdd(e.eventName, e.properties || {}, e.eventType || "auto"));
  }

  // -------------------------
  // AUTO EVENTS (GA-style)
  // -------------------------
  _trackAuto(name, props = {}) {
    if (this._disabled) return;
    if (this.cfg?.debug) console.log("[Twinalyze][AUTO]", name, props);

    if (!this.sessionReady) {
      this.pendingEvents.push({ eventName: name, properties: props, ts: Date.now(), eventType: "auto" });
      return;
    }

    this._emitEventAdd(name, props, "auto");
  }

  _setupEnhancedMeasurement() {
    const em = this.cfg.enhancedMeasurement || {};

    // page_view (normal + SPA)
    if (em.pageViews) {
      const pv = this._pageViewInfo();
      this._trackAuto("page_view", pv);

      const fire = () => {
        this.scrollFired = false;
        const pv2 = this._pageViewInfo();
        this._trackAuto("page_view", pv2);
        this._fireSiteSearch();
      };

      const _push = history.pushState;
      const _replace = history.replaceState;

      history.pushState = (...args) => { _push.apply(history, args); fire(); };
      history.replaceState = (...args) => { _replace.apply(history, args); fire(); };
      window.addEventListener("popstate", fire);
    }

    // scroll (90%)
    if (em.scrolls) {
      window.addEventListener("scroll", () => {
        if (this.scrollFired) return;

        const doc = document.documentElement;
        const scrollTop = window.scrollY || doc.scrollTop;
        const scrollHeight = doc.scrollHeight - doc.clientHeight;
        if (scrollHeight <= 0) return;

        const percent = Math.round((scrollTop / scrollHeight) * 100);
        if (percent >= 90) {
          this.scrollFired = true;
          this._trackAuto("scroll", { percent_scrolled: percent, ...this._pageContext() });
        }
      }, { passive: true });
    }

    // // outbound clicks
    // if (em.outboundClicks) {
    //   document.addEventListener("click", (e) => {
    //     const a = e.target && e.target.closest ? e.target.closest("a") : null;
    //     if (!a || !a.href) return;
    //     if (/^(javascript:|mailto:|tel:)/i.test(a.href)) return;

    //     let url;
    //     try { url = new URL(a.href); } catch { return; }
    //     if (url.hostname !== location.hostname) {
    //       console.log("click", { link_url: url.href, link_domain: url.hostname, outbound: true });
    //       this._trackAuto("click", { link_url: url.href, link_domain: url.hostname, outbound: true });
    //     }
    //   }, true);
    // }

    // clicks (links + buttons + dropdown items + generic interactive)
    if (em.outboundClicks) {
      document.addEventListener("click", (e) => {
        const hit = this._getClickTarget(e);
        if (!hit) return;

        const el = hit.el;

        const tag = (el.tagName || "").toLowerCase();
        const text = this._getTextFromElement(el);

        const props = {
          click_type: hit.kind,                 // link | button | dropdown_item | interactive
          element_tag: tag,
          element_text: text || "not_found",    // ✅ innerText/aria-label/title
          element_id: el.id || null,
          element_name: el.getAttribute?.("name") || null,
          element_role: el.getAttribute?.("role") || null,
          element_classes:
            (el.className && typeof el.className === "string") ? el.className.slice(0, 120) : null,
          element_path: this._cssPath(el),
          ...((this._pageContext && this._pageContext()) || {}), // if you added _pageContext()
        };

        // If link, enrich with URL + outbound
        if (hit.kind === "link") {
          const href = el.getAttribute("href") || "";
          if (/^(javascript:|mailto:|tel:)/i.test(href)) return;

          let url;
          try { url = new URL(el.href); } catch { return; }

          props.link_url = url.href;
          props.link_domain = url.hostname;
          props.link_path = url.pathname;
          props.outbound = url.hostname !== location.hostname;
        }

        // Button details
        if (hit.kind === "button") {
          props.button_type = el.getAttribute?.("type") || null;
          props.disabled = !!el.disabled;
        }

        this._trackAuto("click", props);
      }, true);

    }

    // site search
    if (em.siteSearch) this._fireSiteSearch();

    // form interactions
    if (em.formInteractions) {
      const started = new WeakSet();

      document.addEventListener("focusin", (e) => {
        const form = e.target && e.target.closest ? e.target.closest("form") : null;
        if (!form || started.has(form)) return;
        started.add(form);

        this._trackAuto("form_start", {
          form_id: form.id || undefined,
          form_name: form.getAttribute("name") || undefined,
          form_action: form.action || undefined,
          ...this._pageContext(),
        });
      });

      document.addEventListener("submit", (e) => {
        const form = e.target;
        if (!form) return;

        this._trackAuto("form_submit", {
          form_id: form.id || undefined,
          form_name: form.getAttribute("name") || undefined,
          form_action: form.action || undefined,
          form_destination: location.href,
          ...this._pageContext(),
        });
      }, true);
    }

    // file downloads
    if (em.fileDownloads && em.fileDownloads.extensions) {
      const exts = em.fileDownloads.extensions.map((x) => String(x).toLowerCase());

      document.addEventListener("click", (e) => {
        const a = e.target && e.target.closest ? e.target.closest("a") : null;
        if (!a || !a.href) return;

        const clean = a.href.split("?")[0].toLowerCase();
        const ext = clean.split(".").pop() || "";
        if (!exts.includes(ext)) return;

        this._trackAuto("file_download", {
          link_url: a.href,
          file_extension: ext,
          file_name: clean.split("/").pop(),
        });
      }, true);
    }
  }

  _fireSiteSearch() {
    const em = this.cfg.enhancedMeasurement || {};
    if (!em.siteSearch) return;

    const params = em.siteSearch.params || ["q", "s", "search", "query"];
    const usp = new URLSearchParams(location.search);
    const key = params.find((k) => usp.get(k));
    const term = key ? usp.get(key) : null;

    if (term && term.trim()) {
      this._trackAuto("view_search_results", {
        search_term: term.trim(),
        search_param: key,
        page_path: location.pathname,
        ...this._pageContext()
      });
    }
  }

  _getTextFromElement(el) {
    if (!el) return null;

    // common places (MUI/AntD often store label in aria-label)
    const aria = el.getAttribute?.("aria-label");
    if (aria && aria.trim()) return aria.trim();

    const title = el.getAttribute?.("title");
    if (title && title.trim()) return title.trim();

    // visible text
    const txt = (el.innerText || el.textContent || "").trim();
    if (!txt) return null;

    // limit length (avoid huge HTML)
    return txt.length > 80 ? txt.slice(0, 80) : txt;
  }

  _cssPath(el) {
    if (!el || !el.tagName) return null;
    const parts = [];
    let node = el;
    let depth = 0;
    while (node && node.nodeType === 1 && depth < 5) {
      let part = node.tagName.toLowerCase();
      if (node.id) {
        part += `#${node.id}`;
        parts.unshift(part);
        break;
      }
      const cls = (node.className && typeof node.className === "string")
        ? node.className.trim().split(/\s+/).slice(0, 2).join(".")
        : "";
      if (cls) part += `.${cls}`;
      parts.unshift(part);
      node = node.parentElement;
      depth++;
    }
    return parts.join(" > ");
  }

  _getClickTarget(e) {
    // Shadow DOM safe path
    const path = typeof e.composedPath === "function" ? e.composedPath() : null;
    const start = (path && path.length ? path[0] : e.target);

    const closest = (el, sel) => (el && el.closest ? el.closest(sel) : null);

    // 1) Links
    const a = closest(start, 'a[href]');
    if (a) return { el: a, kind: "link" };

    // 2) Buttons + button-like
    const btn = closest(
      start,
      'button, input[type="button"], input[type="submit"], input[type="reset"], [role="button"]'
    );
    if (btn) return { el: btn, kind: "button" };

    // 3) Menu / dropdown item (generic roles)
    const menuItem = closest(
      start,
      '[role="menuitem"], [role="menuitemradio"], [role="menuitemcheckbox"], [role="option"]'
    );
    if (menuItem) return { el: menuItem, kind: "dropdown_item" };

    // 4) Generic "interactive" fallback:
    //    - role link
    //    - tabindex (focusable)
    //    - aria-haspopup (opens menu)
    //    - contenteditable
    const interactive = closest(
      start,
      '[role="link"], [tabindex]:not([tabindex="-1"]), [aria-haspopup], [contenteditable="true"]'
    );
    if (interactive) return { el: interactive, kind: "interactive" };

    return null;
  }


  // -------------------------
  // PROPERTIES (AUTO)
  // -------------------------
  // _deviceProperties() {
  //   return {
  //     ua: navigator.userAgent,
  //     lang: navigator.language,
  //     tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
  //     screen: { w: screen.width, h: screen.height },
  //     viewport: { w: window.innerWidth, h: window.innerHeight },
  //     dpr: window.devicePixelRatio || 1,
  //   };
  // }

  _deviceProperties() {
    const parser = new UAParser(navigator.userAgent);
    const ua = parser.getResult();

    const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;

    const screenMode =
      window.matchMedia?.("(prefers-color-scheme: dark)")?.matches ? "dark" : "light";

    return {
      // device/browser
      device_type: ua.device?.type || (matchMedia("(pointer:coarse)").matches ? "mobile" : "desktop"),
      os_name: ua.os?.name || "not_found",
      os_version: ua.os?.version || "not_found",
      browser_name: ua.browser?.name || "not_found",
      browser_version: ua.browser?.version || "not_found",

      // app/sdk
      sdk: "web",
      sdk_version: this.cfg?.version || "not_found",

      // screen
      device_screen_mode: screenMode,
      screen_w: String(screen.width),
      screen_h: String(screen.height),
      viewport_w: String(window.innerWidth),
      viewport_h: String(window.innerHeight),
      density: String(window.devicePixelRatio || 1),
      color_depth: String(screen.colorDepth || "not_found"),

      // system-ish
      device_language: navigator.language || "not_found",
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "not_found",
      cores: navigator.hardwareConcurrency != null ? String(navigator.hardwareConcurrency) : "not_found",
      memory_gb: navigator.deviceMemory != null ? String(navigator.deviceMemory) : "not_found",
      touch_points: navigator.maxTouchPoints != null ? String(navigator.maxTouchPoints) : "0",

      // network (best-effort)
      is_data_on: navigator.onLine ? "true" : "false",
      connection_effective_type: conn?.effectiveType || "not_found",
      connection_downlink: conn?.downlink != null ? String(conn.downlink) : "not_found",
      connection_rtt: conn?.rtt != null ? String(conn.rtt) : "not_found",
      connection_save_data: conn?.saveData != null ? String(conn.saveData) : "not_found",

      // raw
      user_agent: navigator.userAgent,
    };
  }

  _userProperties() {
    const url = new URL(location.href);
    const usp = url.searchParams;

    const get = (k) => usp.get(k);
    const nf = "not_found";

    return {
      // acquisition
      landing_url: url.href,
      referrer: [
        {
          utm_source: get("utm_source") || nf,
        },
        {
          utm_medium: get("utm_medium") || nf,
        },
        {
          utm_campaign: get("utm_campaign") || nf,
        },
        {
          utm_term: get("utm_term") || nf,
        },
        {
          utm_content: get("utm_content") || nf,
        }
      ],

      // ads click ids
      gclid: get("gclid") || nf,
      fbclid: get("fbclid") || nf,
      msclkid: get("msclkid") || nf,

      // user preferences
      language_pref: navigator.language || nf,

      // identity (optional, only if you have it)
      // user_id: this.userId || nf,
      // email: this.email || nf,
      // plan: "free" / "pro" etc.
    };
  }


  // -------------------------
  // IDs
  // -------------------------
  _getOrCreateDeviceId() {
    const key = "twinalyze_device_id";
    const old = localStorage.getItem(key);
    if (old) return old;

    const id = uuid();
    // const id = `dev_${uuid()}`;
    localStorage.setItem(key, id);
    return id;
  }

  _pageContext() {
    const KEY_LAST = "twinalyze_last_page_location";

    const pageLocation = location.href;
    const pageDomain = location.hostname;
    const pagePath = location.pathname;
    const pageTitle = document.title;

    const storedPrev = sessionStorage.getItem(KEY_LAST);
    const prevLocation = storedPrev || (document.referrer || null);

    let prevType = "direct";
    if (prevLocation) {
      try {
        const prevHost = new URL(prevLocation).hostname;
        prevType = prevHost === pageDomain ? "internal" : "external";
      } catch {
        prevType = "external";
      }
    }

    return {
      page_domain: pageDomain,
      page_location: pageLocation,
      page_url: pageLocation,
      page_path: pagePath,
      page_title: pageTitle,
      previous_page_location: prevLocation,
      previous_page_type: prevType,
    };
  }

  // _pageViewInfo() {
  //   const KEY_LAST = "twinalyze_last_page_location";
  //   const KEY_COUNT = "twinalyze_page_counter";

  //   const ctx = this._pageContext();

  //   const prevCount = parseInt(sessionStorage.getItem(KEY_COUNT) || "0", 10);
  //   const pageCounter = (Number.isNaN(prevCount) ? 0 : prevCount) + 1;

  //   sessionStorage.setItem(KEY_COUNT, String(pageCounter));
  //   sessionStorage.setItem(KEY_LAST, ctx.page_location);

  //   return {
  //     page_counter: pageCounter,
  //     ...ctx,
  //   };
  // }

  _pageViewInfo() {
    const KEY_LAST = "twinalyze_last_page_location";
    const KEY_COUNT = "twinalyze_page_counter";
    const KEY_LAST_COUNTED = "twinalyze_last_counted_path"; // ✅ new

    const ctx = this._pageContext(); // uses current location + previous page

    const currentKey = ctx.page_path; // ✅ count only on path change
    // If you want count only on full URL change, use: const currentKey = ctx.page_location;

    const lastCounted = sessionStorage.getItem(KEY_LAST_COUNTED);

    // If same page refreshed -> DO NOT increment
    let pageCounter = parseInt(sessionStorage.getItem(KEY_COUNT) || "0", 10);
    if (Number.isNaN(pageCounter)) pageCounter = 0;

    if (lastCounted !== currentKey) {
      pageCounter += 1;
      sessionStorage.setItem(KEY_COUNT, String(pageCounter));
      sessionStorage.setItem(KEY_LAST_COUNTED, currentKey);
    } else {
      // keep same counter
      sessionStorage.setItem(KEY_COUNT, String(pageCounter));
    }

    // update last page location (for prev page logic)
    sessionStorage.setItem(KEY_LAST, ctx.page_location);

    return {
      page_counter: pageCounter,
      ...ctx,
    };
  }
}

const _instance = new TwinalyzeAnalyticsImpl();

const PUBLIC_METHODS = [
  "init",
  "track"
];

const TwinalyzeAnalytics = Object.fromEntries(
  PUBLIC_METHODS.map((m) => [m, _instance[m].bind(_instance)])
);
export { TwinalyzeAnalytics };
export default TwinalyzeAnalytics;